import api from "@/lib/api";

function bandColor(band) {
  switch (band) {
    case "GO": case "STRONG CLARITY": return "#16a34a";
    case "PROMISING": return "#7c3aed";
    case "KEEP TESTING": return "#3b82f6";
    default: return "#f97316";
  }
}

function loadImage(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function wrap(ctx, txt, x, y, maxW, lh, maxLines = 2) {
  const words = (txt || "").split(" ");
  let line = "", yy = y, lines = 0;
  for (const w of words) {
    const test = line + w + " ";
    if (ctx.measureText(test).width > maxW && line) {
      ctx.fillText(line.trim(), x, yy); line = w + " "; yy += lh; lines++;
      if (lines >= maxLines - 1) break;
    } else line = test;
  }
  ctx.fillText(line.trim(), x, yy);
  return yy + lh;
}

const CAT_META = { problem: 20, customer: 15, market: 20, competition: 10, offer: 10, demand: 15, validation: 10 };

export const FORMATS = {
  post: { w: 1080, h: 1350 },
  story: { w: 1080, h: 1920 },
  reel: { w: 1080, h: 1920 },
};

// Build the cpenz progress card for a given format using REAL project data only.
export async function buildCard(project, format = "post", labels = {}) {
  const { w: W, h: H } = FORMATS[format] || FORMATS.post;
  const c = document.createElement("canvas");
  c.width = W; c.height = H;
  const ctx = c.getContext("2d");
  const M = 90; // side margin

  // background
  ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, W, H);
  const rg = ctx.createRadialGradient(W * 0.8, H * 0.12, 40, W * 0.8, H * 0.12, W * 0.9);
  rg.addColorStop(0, "rgba(124,58,237,0.16)"); rg.addColorStop(0.5, "rgba(59,130,246,0.08)"); rg.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = rg; ctx.fillRect(0, 0, W, H);

  const icon = await loadImage("/cpenz-icon.png");

  // header wordmark
  let y = H * 0.075;
  ctx.textAlign = "left";
  if (icon) ctx.drawImage(icon, M, y - 44, 56, 56 * (icon.height / icon.width));
  ctx.fillStyle = "#0b0f1a"; ctx.font = "700 40px Sora, sans-serif";
  ctx.fillText("cpenz", M + 68, y);

  // micro-label + project name
  y = H * (format === "post" ? 0.16 : 0.155);
  ctx.fillStyle = "#7c3aed"; ctx.font = "600 26px Sora, sans-serif";
  ctx.fillText((labels.myIdea || "MY BUSINESS IDEA").toUpperCase(), M, y);
  y += 70;
  ctx.fillStyle = "#0b0f1a"; ctx.font = "700 84px Sora, sans-serif";
  y = wrap(ctx, project?.name || "—", M, y, W - M * 2, 92, 2);

  // maturity pill (public, non-sensitive)
  if (labels.maturity) {
    y += 34;
    ctx.font = "600 26px Sora, sans-serif";
    const txt = labels.maturity.toUpperCase();
    const pw = ctx.measureText(txt).width + 48;
    ctx.fillStyle = "rgba(124,58,237,0.10)"; ctx.beginPath(); ctx.roundRect(M, y - 34, pw, 52, 26); ctx.fill();
    ctx.fillStyle = "#7c3aed"; ctx.fillText(txt, M + 24, y);
    ctx.fillStyle = "#94a3b8"; ctx.fillText((labels.stage || "STAGE").toUpperCase(), M + pw + 20, y);
  }

  // SCORE (Cpenz Clarity Index™ when available)
  const score = project?.score?.total;
  const band = project?.score?.band || "";
  const col = bandColor(band);
  const comps = project?.score?.components;
  y = H * (format === "post" ? 0.40 : 0.39);
  ctx.fillStyle = "#94a3b8"; ctx.font = "600 26px Sora, sans-serif";
  ctx.fillText((labels.score || (comps ? "Cpenz CLARITY INDEX™" : "Cpenz SCORE")).toUpperCase().replace("CPENZ", "cpenz"), M, y);
  if (score != null) {
    y += 130;
    ctx.fillStyle = "#0b0f1a"; ctx.font = "800 150px Sora, sans-serif";
    ctx.fillText(String(score), M, y);
    const nW = ctx.measureText(String(score)).width;
    ctx.fillStyle = "#cbd5e1"; ctx.font = "600 48px Sora, sans-serif";
    ctx.fillText("/ 100", M + nW + 24, y);
    y += 60;
    ctx.fillStyle = col; ctx.font = "700 40px Sora, sans-serif";
    ctx.fillText((labels.bandLabel || band).toUpperCase(), M, y);

    if (comps) {
      const COMP_COL = { clarity: "#7c3aed", evidence: "#3b82f6", coherence: "#a78bfa", validation: "#06b6d4" };
      y += 70;
      ["clarity", "evidence", "coherence", "validation"].forEach((k) => {
        const val = comps[k] ?? 0;
        ctx.fillStyle = "#475569"; ctx.font = "500 30px Sora, sans-serif"; ctx.textAlign = "left";
        ctx.fillText((labels.comp?.[k] || k.charAt(0).toUpperCase() + k.slice(1)).toUpperCase(), M, y);
        ctx.textAlign = "right";
        ctx.fillStyle = "#0b0f1a"; ctx.font = "700 30px Sora, sans-serif";
        ctx.fillText(`${val}`, W - M, y);
        ctx.textAlign = "left";
        const barY = y + 16, barW = W - M * 2;
        ctx.fillStyle = "#eef1f7"; ctx.beginPath(); ctx.roundRect(M, barY, barW, 10, 5); ctx.fill();
        ctx.fillStyle = COMP_COL[k]; ctx.beginPath(); ctx.roundRect(M, barY, barW * (val / 100), 10, 5); ctx.fill();
        y += 62;
      });
    }
  } else {
    y += 60;
    ctx.fillStyle = "#cbd5e1"; ctx.font = "600 44px Sora, sans-serif";
    ctx.fillText("—", M, y);
  }

  // PROGRESS (counts only — never answers, evidence content or next move)
  y = H * (format === "post" ? 0.80 : 0.82);
  ctx.textAlign = "left";
  const chips = [labels.decisions, labels.evidence].filter(Boolean);
  let cx = M;
  chips.forEach((txt) => {
    ctx.font = "600 28px Sora, sans-serif";
    const pw = ctx.measureText(txt).width + 56;
    ctx.fillStyle = "#f4f6fa"; ctx.beginPath(); ctx.roundRect(cx, y - 40, pw, 64, 32); ctx.fill();
    ctx.fillStyle = "#0b0f1a"; ctx.fillText(txt, cx + 28, y + 2);
    cx += pw + 20;
  });

  // footer
  ctx.textAlign = "left";
  ctx.fillStyle = "#0b0f1a"; ctx.font = "600 30px Sora, sans-serif";
  ctx.fillText("Cpenz · IDEAS TAKE SHAPE.", M, H - 118);
  ctx.fillStyle = "#7c3aed"; ctx.font = "700 32px Sora, sans-serif";
  ctx.fillText((labels.build || "BUILD YOUR OWN") + " → cpenz.io", M, H - 70);

  const blob = await new Promise((r) => c.toBlob((b) => r(b), "image/png"));
  return { blob, dataUrl: c.toDataURL("image/png") };
}

// Journey card: generic milestones + score line only. Never answers, evidence content, next move text.

// Score-only card: the cpenz Clarity Index™ and its four components. No idea title, nothing private.
export async function buildScoreCard(project, format = "post", labels = {}) {
  const { w: W, h: H } = FORMATS[format] || FORMATS.post;
  const c = document.createElement("canvas");
  c.width = W; c.height = H;
  const ctx = c.getContext("2d");
  const M = 90;
  const score = project?.score?.total;
  const band = project?.score?.band || "";
  const comps = project?.score?.components || {};
  const col = bandColor(band);

  ctx.fillStyle = "#0b0f1a"; ctx.fillRect(0, 0, W, H);
  const rg = ctx.createRadialGradient(W * 0.85, H * 0.1, 40, W * 0.85, H * 0.1, W * 1.1);
  rg.addColorStop(0, "rgba(124,58,237,0.45)"); rg.addColorStop(0.45, "rgba(59,130,246,0.18)"); rg.addColorStop(1, "rgba(11,15,26,0)");
  ctx.fillStyle = rg; ctx.fillRect(0, 0, W, H);
  const rg2 = ctx.createRadialGradient(W * 0.1, H * 0.95, 20, W * 0.1, H * 0.95, W * 0.9);
  rg2.addColorStop(0, "rgba(6,182,212,0.22)"); rg2.addColorStop(1, "rgba(11,15,26,0)");
  ctx.fillStyle = rg2; ctx.fillRect(0, 0, W, H);

  const icon = await loadImage("/cpenz-icon.png");
  let y = H * 0.075;
  ctx.textAlign = "left";
  if (icon) ctx.drawImage(icon, M, y - 44, 56, 56 * (icon.height / icon.width));
  ctx.fillStyle = "#ffffff"; ctx.font = "700 40px Sora, sans-serif";
  ctx.fillText("cpenz", M + 68, y);

  y = H * (format === "post" ? 0.19 : 0.2);
  ctx.fillStyle = "#a78bfa"; ctx.font = "600 26px Sora, sans-serif";
  ctx.fillText((labels.label || "Cpenz CLARITY INDEX™").toUpperCase().replace("CPENZ", "cpenz"), M, y);
  y += 58;
  ctx.fillStyle = "#ffffff"; ctx.font = "700 64px Sora, sans-serif";
  y = wrap(ctx, (labels.headline || "My CCI is {n}.").replace("{n}", score ?? "—"), M, y, W - M * 2, 74, 2);

  y += format === "post" ? 150 : 190;
  ctx.fillStyle = "#ffffff"; ctx.font = "800 260px Sora, sans-serif";
  ctx.fillText(String(score ?? "—"), M, y);
  const nW = ctx.measureText(String(score ?? "—")).width;
  ctx.fillStyle = "rgba(255,255,255,0.35)"; ctx.font = "600 56px Sora, sans-serif";
  ctx.fillText("/ 100", M + nW + 28, y);
  y += 74;
  if (band) {
    ctx.font = "700 30px Sora, sans-serif";
    const txt = (labels.bandLabel || band).toUpperCase();
    const pw = ctx.measureText(txt).width + 56;
    ctx.fillStyle = `${col}33`; ctx.beginPath(); ctx.roundRect(M, y - 38, pw, 58, 29); ctx.fill();
    ctx.fillStyle = col; ctx.fillText(txt, M + 28, y + 3);
  }

  y += format === "post" ? 120 : 170;
  const COMP_COL = { clarity: "#a78bfa", evidence: "#60a5fa", coherence: "#c4b5fd", validation: "#22d3ee" };
  const barW = W - M * 2;
  ["clarity", "evidence", "coherence", "validation"].forEach((k) => {
    const val = comps[k] ?? 0;
    ctx.textAlign = "left"; ctx.fillStyle = "rgba(255,255,255,0.7)"; ctx.font = "600 30px Sora, sans-serif";
    ctx.fillText((labels.comp?.[k] || k).toUpperCase(), M, y);
    ctx.textAlign = "right"; ctx.fillStyle = "#ffffff"; ctx.font = "700 34px Sora, sans-serif";
    ctx.fillText(`${val}`, W - M, y);
    ctx.textAlign = "left";
    ctx.fillStyle = "rgba(255,255,255,0.10)"; ctx.beginPath(); ctx.roundRect(M, y + 18, barW, 14, 7); ctx.fill();
    ctx.fillStyle = COMP_COL[k]; ctx.beginPath(); ctx.roundRect(M, y + 18, Math.max(14, barW * (val / 100)), 14, 7); ctx.fill();
    y += format === "post" ? 92 : 110;
  });

  ctx.fillStyle = "rgba(255,255,255,0.5)"; ctx.font = "500 26px Sora, sans-serif";
  wrap(ctx, labels.note || "", M, y + 30, W - M * 2, 34, 2);

  ctx.textAlign = "left";
  ctx.fillStyle = "#ffffff"; ctx.font = "700 30px Sora, sans-serif";
  ctx.fillText((labels.tagline || "LESS GUESSING. MORE EVIDENCE.").toUpperCase(), M, H - 118);
  ctx.fillStyle = "#a78bfa"; ctx.font = "600 26px Sora, sans-serif";
  ctx.fillText("cpenz.io", M, H - 74);
  ctx.textAlign = "right"; ctx.fillStyle = "rgba(255,255,255,0.45)"; ctx.font = "500 24px Sora, sans-serif";
  ctx.fillText("IDEAS TAKE SHAPE.", W - M, H - 74);

  const blob = await new Promise((r) => c.toBlob((b) => r(b), "image/png"));
  return { blob, dataUrl: c.toDataURL("image/png") };
}

export async function buildJourneyCard(project, journey, format = "post", labels = {}) {
  const { w: W, h: H } = FORMATS[format] || FORMATS.post;
  const c = document.createElement("canvas");
  c.width = W; c.height = H;
  const ctx = c.getContext("2d");
  const M = 90;

  ctx.fillStyle = "#0b0f1a"; ctx.fillRect(0, 0, W, H);
  const rg = ctx.createRadialGradient(W * 0.85, H * 0.1, 40, W * 0.85, H * 0.1, W * 0.9);
  rg.addColorStop(0, "rgba(124,58,237,0.45)"); rg.addColorStop(0.5, "rgba(59,130,246,0.15)"); rg.addColorStop(1, "rgba(11,15,26,0)");
  ctx.fillStyle = rg; ctx.fillRect(0, 0, W, H);

  const icon = await loadImage("/cpenz-icon.png");
  let y = H * 0.075;
  ctx.textAlign = "left";
  if (icon) ctx.drawImage(icon, M, y - 44, 56, 56 * (icon.height / icon.width));
  ctx.fillStyle = "#ffffff"; ctx.font = "700 40px Sora, sans-serif";
  ctx.fillText("cpenz", M + 68, y);

  y = H * (format === "post" ? 0.15 : 0.14);
  ctx.fillStyle = "#a78bfa"; ctx.font = "600 26px Sora, sans-serif";
  ctx.fillText((labels.title || "MY JOURNEY").toUpperCase(), M, y);
  y += 66;
  ctx.fillStyle = "#ffffff"; ctx.font = "700 76px Sora, sans-serif";
  y = wrap(ctx, project?.name || "—", M, y, W - M * 2, 84, 2);
  ctx.fillStyle = "#94a3b8"; ctx.font = "500 30px Sora, sans-serif";
  const meta = [labels.maturity, labels.days].filter(Boolean).join("  ·  ");
  ctx.fillText(meta, M, y + 6);

  // score line
  const pts = journey.scores || [];
  if (pts.length >= 1) {
    const top = y + 70, hgt = format === "post" ? 150 : 200, left = M, width = W - M * 2;
    ctx.fillStyle = "#64748b"; ctx.font = "600 22px Sora, sans-serif";
    ctx.fillText((labels.score || "BUSINESS SCORE").toUpperCase(), left, top);
    const gy = top + 30, min = Math.min(...pts, 0), max = Math.max(...pts, 100);
    const px = (i) => pts.length === 1 ? left + width / 2 : left + (i / (pts.length - 1)) * width;
    const py = (v) => gy + hgt - ((v - min) / (max - min || 1)) * hgt;
    ctx.strokeStyle = "rgba(255,255,255,0.08)"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(left, gy + hgt); ctx.lineTo(left + width, gy + hgt); ctx.stroke();
    const grad = ctx.createLinearGradient(left, 0, left + width, 0);
    grad.addColorStop(0, "#7c3aed"); grad.addColorStop(1, "#3b82f6");
    ctx.strokeStyle = grad; ctx.lineWidth = 8; ctx.lineJoin = "round"; ctx.lineCap = "round";
    ctx.beginPath(); pts.forEach((v, i) => (i ? ctx.lineTo(px(i), py(v)) : ctx.moveTo(px(i), py(v)))); ctx.stroke();
    pts.forEach((v, i) => {
      ctx.fillStyle = "#0b0f1a"; ctx.beginPath(); ctx.arc(px(i), py(v), 14, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "#ffffff"; ctx.beginPath(); ctx.arc(px(i), py(v), 8, 0, Math.PI * 2); ctx.fill();
      ctx.font = "700 28px Sora, sans-serif"; ctx.textAlign = i === 0 ? "left" : i === pts.length - 1 ? "right" : "center";
      ctx.fillText(String(v), px(i), py(v) - 26);
    });
    ctx.textAlign = "left";
    y = gy + hgt + 50;
  } else {
    y += 60;
  }

  // milestones
  const steps = (journey.steps || []).slice(0, format === "post" ? 6 : 8);
  const rowH = format === "post" ? 92 : 104;
  const startY = Math.max(y + 40, H * (format === "post" ? 0.52 : 0.44));
  steps.forEach((s, i) => {
    const yy = startY + i * rowH;
    if (i < steps.length - 1) { ctx.strokeStyle = "rgba(255,255,255,0.12)"; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(M + 14, yy + 18); ctx.lineTo(M + 14, yy + rowH - 14); ctx.stroke(); }
    ctx.fillStyle = i === steps.length - 1 ? "#a78bfa" : "#ffffff"; ctx.beginPath(); ctx.arc(M + 14, yy, 10, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = "#64748b"; ctx.font = "600 22px Sora, sans-serif"; ctx.fillText(s.date, M + 48, yy - 14);
    ctx.fillStyle = "#ffffff"; ctx.font = "600 34px Sora, sans-serif";
    let txt = s.label; while (ctx.measureText(txt).width > W - M * 2 - 60 && txt.length > 8) txt = txt.slice(0, -2) + "…";
    ctx.fillText(txt, M + 48, yy + 26);
  });

  ctx.textAlign = "left";
  ctx.fillStyle = "#ffffff"; ctx.font = "600 30px Sora, sans-serif";
  ctx.fillText("Cpenz BUSINESS IDEA", M, H - 118);
  ctx.fillStyle = "#a78bfa"; ctx.font = "700 32px Sora, sans-serif";
  ctx.fillText((labels.build || "BUILD YOUR OWN") + " → cpenz.io", M, H - 70);

  const blob = await new Promise((r) => c.toBlob((b) => r(b), "image/png"));
  return { blob, dataUrl: c.toDataURL("image/png") };
}

export function downloadCard(blob, format) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `cpenz-${format}.png`; a.click();
  URL.revokeObjectURL(url);
}

export async function shareBlob(blob, name, title) {
  const file = new File([blob], name, { type: "image/png" });
  if (navigator.canShare && navigator.canShare({ files: [file] })) {
    try { await navigator.share({ files: [file], title }); return "shared"; }
    catch (e) { if (e && e.name === "AbortError") return "cancel"; }
  }
  downloadCard(blob, name.replace(/^cpenz-|\.png$/g, ""));
  window.open("https://www.instagram.com/", "_blank", "noopener");
  return "download";
}

export async function shareCard(project, blob, format) {
  try { await api.post(`/projects/${project.id}/share`, {}); } catch (e) {}
  return shareBlob(blob, `cpenz-${format}.png`, project?.name);
}

// Random-idea card (public generator): pop design, palette picked from the idea so every card looks different.
const POP_PALETTES = [
  { bg: "#7c3aed", ink: "#FFFFFF", pop: "#c4b5fd", pop2: "#0b0f1a", paper: "#0b0f1a" },
  { bg: "#0b0f1a", ink: "#FFFFFF", pop: "#7c3aed", pop2: "#c4b5fd", paper: "#FFFFFF" },
  { bg: "#FFFFFF", ink: "#0b0f1a", pop: "#7c3aed", pop2: "#0b0f1a", paper: "#0b0f1a" },
  { bg: "#5b21b6", ink: "#FFFFFF", pop: "#a78bfa", pop2: "#ede9fe", paper: "#FFFFFF" },
  { bg: "#ede9fe", ink: "#0b0f1a", pop: "#7c3aed", pop2: "#0b0f1a", paper: "#FFFFFF" },
  { bg: "#a78bfa", ink: "#0b0f1a", pop: "#0b0f1a", pop2: "#FFFFFF", paper: "#FFFFFF" },
];
function hashStr(str) { let h = 0; for (const ch of str || "") h = (h * 31 + ch.charCodeAt(0)) | 0; return Math.abs(h); }
function wrapLines(ctx, txt, maxW, maxLines) {
  const words = (txt || "").split(" ").filter(Boolean), lines = []; let line = "", i = 0;
  for (; i < words.length; i++) {
    const w = words[i], test = line ? line + " " + w : w;
    if (ctx.measureText(test).width > maxW && line) {
      lines.push(line); line = w;
      if (lines.length === maxLines) { line = ""; break; }
    } else line = test;
  }
  if (line && lines.length < maxLines) lines.push(line);
  else if (line || i < words.length) lines[maxLines - 1] = lines[maxLines - 1].replace(/[,.;:]?$/, "") + "…";
  return lines;
}
function sticker(ctx, text, x, y, rot, fill, ink, font = "800 30px Sora, sans-serif", outline = "#0b0f1a") {
  ctx.save(); ctx.translate(x, y); ctx.rotate(rot); ctx.font = font;
  const w = ctx.measureText(text).width + 56, h = 66;
  ctx.fillStyle = outline; ctx.beginPath(); ctx.roundRect(-w / 2 + 8, -h / 2 + 8, w, h, 33); ctx.fill();
  ctx.fillStyle = fill; ctx.beginPath(); ctx.roundRect(-w / 2, -h / 2, w, h, 33); ctx.fill();
  ctx.lineWidth = 4; ctx.strokeStyle = outline; ctx.stroke();
  ctx.fillStyle = ink; ctx.textAlign = "center"; ctx.textBaseline = "middle"; ctx.fillText(text, 0, 2); ctx.restore();
}

export async function buildRandomIdeaCard(idea, format = "post", labels = {}) {
  const { w: W, h: H } = FORMATS[format] || FORMATS.post;
  const c = document.createElement("canvas");
  c.width = W; c.height = H;
  const ctx = c.getContext("2d");
  const pal = POP_PALETTES[hashStr(idea?.title + idea?.idea) % POP_PALETTES.length];
  const M = 84, story = format !== "post";

  // background + halftone dots + big shapes
  ctx.fillStyle = pal.bg; ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = pal.ink === "#FFFFFF" ? "rgba(255,255,255,0.14)" : "rgba(11,15,26,0.12)";
  for (let y = 40; y < H; y += 44) for (let x = 40 + ((y / 44) % 2) * 22; x < W; x += 44) {
    const r = 3 + 6 * Math.max(0, Math.min(1, (x / W) * 0.6 + (y / H) * 0.6 - 0.35));
    ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
  }
  const line = pal.ink === "#FFFFFF" ? "#FFFFFF" : "#0b0f1a";
  ctx.fillStyle = pal.pop; ctx.beginPath(); ctx.arc(W * 0.9, H * 0.12, W * 0.28, 0, Math.PI * 2); ctx.fill();
  ctx.lineWidth = 8; ctx.strokeStyle = line; ctx.stroke();
  ctx.fillStyle = pal.pop2; ctx.beginPath(); ctx.arc(W * 0.08, H * 0.86, W * 0.22, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
  ctx.save(); ctx.translate(W * 0.86, H * 0.88); ctx.rotate(-0.35);
  for (let i = 0; i < 5; i++) { ctx.fillStyle = i % 2 ? pal.pop : line; ctx.fillRect(-160, -90 + i * 36, 320, 18); }
  ctx.restore();

  // header
  ctx.textBaseline = "alphabetic"; ctx.textAlign = "left";
  ctx.fillStyle = pal.ink; ctx.font = "800 40px Sora, sans-serif"; ctx.fillText("cpenz.io", M, M + 36);
  sticker(ctx, (labels.kicker || "RANDOM BUSINESS IDEA").toUpperCase(), W * 0.62, M + 120, -0.08, pal.paper, pal.paper === "#FFFFFF" ? "#0b0f1a" : "#FFFFFF", "800 30px Sora, sans-serif", line);

  // paper card: height follows the content (measured first), hard offset shadow
  const cardY = story ? H * 0.19 : H * 0.185, cardX = M - 12, cardW = W - (cardX) * 2, px = cardX + 60, pw = cardW - 120;
  const TF = story ? "800 82px Sora, sans-serif" : "800 66px Sora, sans-serif", TLH = story ? 90 : 74;
  const DF = story ? "500 38px Sora, sans-serif" : "500 33px Sora, sans-serif", DLH = story ? 52 : 44;
  const WF = story ? "italic 500 30px Sora, sans-serif" : "italic 500 27px Sora, sans-serif", WLH = story ? 40 : 36;
  ctx.font = TF; const titleLines = wrapLines(ctx, idea?.title || "", pw, 2);
  ctx.font = DF; const descLines = wrapLines(ctx, idea?.idea || "", pw, story ? 7 : 4);
  ctx.font = WF; const whyLines = idea?.why_fun ? wrapLines(ctx, idea.why_fun, pw, 2) : [];
  ctx.font = "700 30px Sora, sans-serif"; const fcLines = idea?.first_customer ? wrapLines(ctx, idea.first_customer, pw, story ? 2 : 1) : [];
  const cardH = 90 + 90 + titleLines.length * TLH + 14 + descLines.length * DLH + (whyLines.length ? 16 + whyLines.length * WLH : 0) + (fcLines.length ? 24 + 36 + fcLines.length * 36 : 0) + 44;
  const shadow = pal.paper === "#0b0f1a" ? "#7c3aed" : "#0b0f1a";
  ctx.fillStyle = shadow; ctx.beginPath(); ctx.roundRect(cardX + 18, cardY + 18, cardW, cardH, 44); ctx.fill();
  ctx.fillStyle = pal.paper; ctx.beginPath(); ctx.roundRect(cardX, cardY, cardW, cardH, 44); ctx.fill();
  ctx.lineWidth = 6; ctx.strokeStyle = pal.paper === "#0b0f1a" ? "#7c3aed" : "#0b0f1a"; ctx.stroke();
  const inkOnPaper = pal.paper === "#FFFFFF" ? "#0b0f1a" : "#FFFFFF";
  const muted = pal.paper === "#FFFFFF" ? "rgba(11,15,26,0.6)" : "rgba(255,255,255,0.7)";

  let y = cardY + 90;
  let bx = px;
  [labels.novelty, labels.creativity].filter(Boolean).forEach((txt, i) => {
    ctx.font = "800 22px Sora, sans-serif"; const t2 = txt.toUpperCase(), bw = ctx.measureText(t2).width + 40;
    const bfill = i ? pal.pop2 : pal.pop;
    ctx.fillStyle = bfill; ctx.beginPath(); ctx.roundRect(bx, y - 34, bw, 48, 24); ctx.fill();
    ctx.lineWidth = 3; ctx.strokeStyle = bfill === "#0b0f1a" ? "#FFFFFF" : "#0b0f1a"; ctx.stroke();
    ctx.fillStyle = (bfill === "#0b0f1a" || bfill === "#7c3aed" || bfill === "#5b21b6") ? "#FFFFFF" : "#0b0f1a"; ctx.fillText(t2, bx + 20, y); bx += bw + 14;
  });
  y += 90;
  ctx.font = TF; ctx.fillStyle = inkOnPaper;
  for (const ln of titleLines) { ctx.fillText(ln, px, y); y += TLH; }
  y += 14;
  ctx.font = DF;
  for (const ln of descLines) { ctx.fillText(ln, px, y); y += DLH; }
  if (whyLines.length) {
    y += 16; ctx.font = WF; ctx.fillStyle = muted;
    for (const ln of whyLines) { ctx.fillText(ln, px, y); y += WLH; }
  }
  if (fcLines.length) {
    y += 24; ctx.font = "800 22px Sora, sans-serif"; ctx.fillStyle = muted; ctx.fillText((labels.first_customer || "FIRST CUSTOMER").toUpperCase(), px, y);
    y += 36; ctx.font = "700 30px Sora, sans-serif"; ctx.fillStyle = inkOnPaper;
    for (const ln of fcLines) { ctx.fillText(ln, px, y); y += 36; }
  }

  // TAG band, sitting on the card's bottom edge (pop sticker, 1-2 lines)
  const tagText = (labels.tag || "TAG THE FIRST CUSTOMER").toUpperCase();
  ctx.font = "800 30px Sora, sans-serif";
  const tagLines = wrapLines(ctx, tagText, W - M * 2 - 120, 2);
  const tagH = tagLines.length === 1 ? 74 : 116, tagW = Math.max(...tagLines.map((l) => ctx.measureText(l).width)) + 80;
  const tagCY = cardY + cardH + 8;
  ctx.save(); ctx.translate(W / 2, tagCY); ctx.rotate(-0.025);
  ctx.fillStyle = line; ctx.beginPath(); ctx.roundRect(-tagW / 2 + 10, -tagH / 2 + 10, tagW, tagH, 24); ctx.fill();
  ctx.fillStyle = "#7c3aed"; ctx.beginPath(); ctx.roundRect(-tagW / 2, -tagH / 2, tagW, tagH, 24); ctx.fill();
  ctx.lineWidth = 5; ctx.strokeStyle = line; ctx.stroke();
  ctx.fillStyle = "#FFFFFF"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
  tagLines.forEach((ln, i) => ctx.fillText(ln, 0, tagLines.length === 1 ? 2 : -20 + i * 42));
  ctx.restore(); ctx.textAlign = "left"; ctx.textBaseline = "alphabetic";

  // speech bubble: comment + share your own idea (kept clear of the footer sticker)
  const bw2 = W - M * 2, bh = 130, footerY = H - (story ? 120 : 92);
  const by = Math.min(tagCY + tagH / 2 + (story ? 110 : 56), footerY - 72 - bh);
  const bubble = pal.bg === "#0b0f1a" ? "#7c3aed" : "#0b0f1a";
  ctx.fillStyle = bubble === "#0b0f1a" ? "#7c3aed" : "#0b0f1a"; ctx.beginPath(); ctx.roundRect(M + 14, by + 14, bw2, bh, 36); ctx.fill();
  ctx.fillStyle = bubble; ctx.beginPath(); ctx.roundRect(M, by, bw2, bh, 36); ctx.fill();
  ctx.beginPath(); ctx.moveTo(M + 90, by); ctx.lineTo(M + 130, by - 36); ctx.lineTo(M + 170, by); ctx.fill();
  ctx.fillStyle = "#FFFFFF"; ctx.font = "800 34px Sora, sans-serif";
  const qLines = wrapLines(ctx, labels.question || "Would you build it? Comment and share your idea", bw2 - 80, 2);
  qLines.forEach((ln, i) => ctx.fillText(ln, M + 40, by + (qLines.length === 1 ? 78 : 56 + i * 42)));

  // footer CTA
  sticker(ctx, (labels.build || "TRY THE IDEA GENERATOR").toUpperCase() + "  →  cpenz.io", W / 2, footerY, 0.03, "#7c3aed", "#FFFFFF", "800 30px Sora, sans-serif", line);

  const blob = await new Promise((r) => c.toBlob((b) => r(b), "image/png"));
  return { blob, dataUrl: c.toDataURL("image/png") };
}
